import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

/** Form generico guidato da config. fields: [{name,label,type:'text'|'number'|'date'|'select'|'textarea'|'checkbox',options:[{value,label}],required,readOnly,help,span}] */
export function FormDialog({ open, onClose, title, fields, initial = {}, onSubmit, submitLabel = "Salva", testId = "form" }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    const init = { ...(initial || {}) };
    fields.forEach((f) => { if (f.type === "percent" && init[f.name] !== null && init[f.name] !== undefined && init[f.name] !== "") init[f.name] = String(Math.round(Number(init[f.name]) * 10000) / 100); });
    setValues(init);  // i campi percentuale sono tenuti nello stato già in "percento" (24 = 24%)
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (n, v) => setValues((s) => ({ ...s, [n]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const out = {};
      fields.forEach((f) => {
        let v = values[f.name];
        if (f.type === "number") v = v === "" || v === undefined || v === null ? (f.nullable ? null : 0) : Number(v);
        if (f.type === "percent") v = v === "" || v === undefined || v === null ? (f.nullable ? null : 0) : Math.round(Number(String(v).replace(",", ".")) * 10000) / 1000000;
        if ((f.type === "text" || f.type === "date" || f.type === "select" || f.type === "textarea") && (v === "" || v === undefined)) v = f.required ? "" : null;
        out[f.name] = v;
      });
      await onSubmit(out);
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  const val = (f) => {
    const v = values[f.name];
    return v ?? "";
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" data-testid={`${testId}-dialog`}>
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((f) => (
            <label key={f.name} className={`flex flex-col gap-1 text-sm ${f.span === 2 ? "md:col-span-2" : ""}`}>
              <span className="text-slate-600 font-medium">{f.label}{f.required && <span className="text-red-700"> *</span>}</span>
              {f.type === "select" ? (
                <select data-testid={`${testId}-${f.name}`} value={val(f)} onChange={(e) => set(f.name, e.target.value)} required={f.required} disabled={f.readOnly} className={f.readOnly ? "input-readonly" : "input-editable"}>
                  <option value="">—</option>
                  {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === "textarea" ? (
                <textarea data-testid={`${testId}-${f.name}`} value={val(f)} onChange={(e) => set(f.name, e.target.value)} rows={3} className={f.readOnly ? "input-readonly" : "input-editable"} readOnly={f.readOnly} />
              ) : f.type === "checkbox" ? (
                <input type="checkbox" data-testid={`${testId}-${f.name}`} checked={!!values[f.name]} onChange={(e) => set(f.name, e.target.checked)} className="w-4 h-4 mt-2 accent-primary" />
              ) : (
                <input data-testid={`${testId}-${f.name}`} type={f.type === "percent" ? "text" : f.type || "text"} inputMode={f.type === "percent" ? "decimal" : undefined} step={f.type === "number" ? "any" : undefined}
                  value={val(f)} onChange={(e) => set(f.name, e.target.value)} required={f.required} readOnly={f.readOnly} placeholder={f.type === "percent" ? "%" : ""}
                  className={f.readOnly ? "input-readonly" : "input-editable"} />
              )}
              {f.help && <span className="text-xs text-slate-500">{f.help}</span>}
            </label>
          ))}
          <DialogFooter className="md:col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid={`${testId}-cancel`}>Annulla</Button>
            <Button type="submit" disabled={saving} data-testid={`${testId}-submit`}>{saving ? "Salvataggio…" : submitLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
